import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SystemSettingsService } from '../../classroom360/system-settings/system-settings.service';
import { SharedService } from '../../shared-components/shared.service';
import { Student } from '../../shared-components/student/student.model';
import { System } from '../../shared-components/System';
import { UserService } from '../../shared-components/user/user.service';
import { YearService } from '../../shared-components/year/year.service';
import { FreeReducedMealAppAdultIncomeSubType } from './models/free-reduced-meal-app-adult-income-sub-type.model';
import { FreeReducedMealAppAdultIncomeType } from './models/free-reduced-meal-app-adult-income-type.model';
import { FreeReducedMealAppAdult } from './models/free-reduced-meal-app-adult.model';
import { FreeReducedMealAppChildIncomeType } from './models/free-reduced-meal-app-child-income-type.model';
import { FreeReducedMealAppChild } from './models/free-reduced-meal-app-child.model';
import { FreeReducedMealAppFamily } from './models/free-reduced-meal-app-family.model';
import { FreeReducedMealAppIncome } from './models/free-reduced-meal-app-income.model';
import { FreeReducedMealAppPageChangeHistory } from './models/free-reduced-meal-app-page-change-history.model';
import { FreeReducedMealAppPageGroup } from './models/free-reduced-meal-app-page-group.model';
import { FreeReducedMealAppPage } from './models/free-reduced-meal-app-page.model';
import { FreeReducedMealAppProgram } from './models/free-reduced-meal-app-program.model';
import { FreeReducedMealAppStudentLunchStatus } from './models/free-reduced-meal-app-student-lunch-status.model';
import { FrmaAdultIncomeTypesEnum, FrmaPagesEnum } from './models/free-reduced-meal-app.enum';
import { FreeReducedMealApp } from './models/free-reduced-meal-app.model';
import { FRMALetterTemplate } from './models/frma-letter-template.model';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { MailParameters } from '../send-email/mail-paremeters';
import { UserPreferencesService } from '../../shared-components/user-preferences/user-preferences.service';
import { SendEmailService } from '../send-email/send-email.service';
import { LoginService } from '../login/login.service';
import { Year } from '../../shared-components/year/year.model';
import { StudentContact } from '../../shared-components/student/student-contact.model';
import { FreeReducedMealAppAcceptedHistory } from './models/free-reduced-meal-app-accepted-history.model';


@Injectable()
export class FreeReducedMealAppService {

  // student/adult income yes/no buttons are marked yes if records exist and no if records do not exist
  // because we require users to click either yes or no, we have to track when an answer is clicked so if no records exist AND they have selected an answer it will be marked no
  // otherwise, if no records exist and the user has not selected an answer, neither yes or no will be selected
  // these dictionaries will track user answers within the current FRMA
  childIncomeClicks: { [studentId: number]: { [incomeTypeId: number]: boolean } } = {};
  adultIncomeSubTypeClicks: { [adultId: number]: { [incomeSubTypeId: number]: boolean } } = {};
  adultIncomeClicks: { [adultId: number]: { [incomeTypeId: number]: boolean } } = {};

  selectedFamily: FreeReducedMealAppFamily;
  selectedLetterTemplate: FRMALetterTemplate;

  subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private yearService: YearService,
    private userService: UserService,
    private sharedService: SharedService,
    private userPreferencesService: UserPreferencesService,
    private sendEmailService: SendEmailService,
    private loginService: LoginService
  ) { }

  sampleData(): FreeReducedMealApp {
    const newApp = new FreeReducedMealApp(this.yearService.defaultYear.Id, -1);
    newApp.YearID = this.yearService.student360Year.Id;
    newApp.FamilyID = this.userService.currentUser.FamilyId;
    newApp.Programs = [];
    newApp.Adults = [];
    // newApp.StudentsInFamily = [];
    newApp.Children = [];
    const bob = new Student();
    bob.FirstName = 'Student1';
    bob.LastName = 'Example';
    bob.Id = 1;
    const suzy = new Student();
    suzy.FirstName = 'Student2';
    suzy.LastName = 'Example';
    suzy.Id = 2;
    // newApp.StudentsInFamily.push(bob);
    // newApp.StudentsInFamily.push(suzy);
    const other1 = new FreeReducedMealAppChild();
    const other2 = new FreeReducedMealAppChild();
    const other3 = new FreeReducedMealAppChild();
    const other4 = new FreeReducedMealAppChild();
    other3.ID = 3;
    other3.FirstName = bob.FirstName;
    other3.LastName = bob.LastName;
    other3.StudentID = bob.Id;
    other3.IsStudent = true;
    other3.IsEnrolled = true;
    other3.Programs = [];
    other4.ID = 4;
    other4.FirstName = suzy.FirstName;
    other4.LastName = suzy.LastName;
    other4.StudentID = suzy.Id;
    other4.IsStudent = true;
    other4.IsEnrolled = true;
    other4.Programs = [];
    other1.ID = 1;
    other1.FirstName = 'Other Student';
    other1.LastName = 'Example';
    other1.IsStudent = true;
    other1.IsEnrolled = false;
    other1.Programs = [];
    other2.ID = 2;
    other2.FirstName = 'Other Non-student';
    other2.LastName = 'Example';
    other2.Programs = [];
    newApp.Children.push(other1);
    newApp.Children.push(other2);
    newApp.Children.push(other3);
    newApp.Children.push(other4);
    const newParent = new FreeReducedMealAppAdult();
    newParent.ID = 1;
    newParent.FirstName = 'Household Parent 1';
    newParent.LastName = 'Example';
    newParent.PeopleID = 1;
    newParent.IncomeTypeIds = [];
    newApp.Adults.push(newParent);
    const newParent2 = new FreeReducedMealAppAdult();
    newParent2.ID = 2;
    newParent2.FirstName = 'Household Parent 2';
    newParent2.LastName = 'Example';
    newParent2.PeopleID = 2;
    newParent2.IncomeTypeIds = [];
    const newParent3 = new FreeReducedMealAppAdult();
    newParent3.ID = 3;
    newParent3.FirstName = 'Other Adult';
    newParent3.LastName = 'Example';
    newParent3.IncomeTypeIds = [];
    newApp.Adults.push(newParent3);
    return newApp;
  }

  formatName(firstName: string, middleName: string, lastName: string, suffix: string): string {
    return (firstName ? firstName : '') + (middleName ? ' ' + middleName : '') + (lastName ? ' ' + lastName : '') + (suffix ? ' ' + suffix : '');

  }

  calculateAnnualHouseholdIncome(app: FreeReducedMealApp): number {
    let annualIncome = 0;
    if (app) {
      if (app.Children) {
        app.Children.forEach(c => {
          if (c.Income) {
            c.Income.forEach(i => {
              annualIncome += this.convertIncomeToAnnual(i);
            });
          }
        });
      }
      if (app.Adults) {
        app.Adults.forEach(c => {
          if (c.Income) {
            c.Income.forEach(i => {
              annualIncome += this.convertIncomeToAnnual(i);
            });
          }
        });
      }
    }
    return annualIncome;
  }

  convertIncomeToAnnual(income: FreeReducedMealAppIncome): number {
    let annualIncome = 0;
    let multiplier = 1;
    switch (income.Frequency.toLowerCase()) {
      case 'yearly':
        multiplier = 1;
        break;
      case 'monthly':
        multiplier = 12;
        break;
      case 'twice per month':
        multiplier = 24;
        break;
      case 'every two weeks':
        multiplier = 26;
        break;
      case 'weekly':
        multiplier = 52;
        break;
      case 'hourly':
        if (income.Per.toLowerCase() == 'week') { // weekly
          multiplier = 52 * (income.Hours ? income.Hours : 0);
        }
        else { // monthly
          multiplier = 12 * (income.Hours ? income.Hours : 0);
        }
        break;
      default:
        break;
    }
    if (isNaN(income.Amount)) {
      income.Amount = 0;
    }
    annualIncome = (income.Amount ? income.Amount : 0) * multiplier;
    return annualIncome;
  }

  getAdultIncomeTypeByPage(pageId: number): number {
    let adultIncomeTypeID: number = null;
    switch (pageId) {
      case FrmaPagesEnum.MilitaryIncome:
        adultIncomeTypeID = FrmaAdultIncomeTypesEnum.Military;
        break;
      case FrmaPagesEnum.EarningsFromWork:
        adultIncomeTypeID = FrmaAdultIncomeTypesEnum.Employment;
        break;
      case FrmaPagesEnum.IncomeFromPublicAssistance:
        adultIncomeTypeID = FrmaAdultIncomeTypesEnum.PublicAssistance;
        break;
      case FrmaPagesEnum.IncomeFromAlimonyorChildSupport:
        adultIncomeTypeID = FrmaAdultIncomeTypesEnum.AlimonyChildSupport;
        break;
      case FrmaPagesEnum.IncomeFromBenefits:
        adultIncomeTypeID = FrmaAdultIncomeTypesEnum.Benefits;
        break;
      case FrmaPagesEnum.RetirementIncome:
        adultIncomeTypeID = FrmaAdultIncomeTypesEnum.Retirement;
        break;
      case FrmaPagesEnum.OtherSourcesofIncome:
        adultIncomeTypeID = FrmaAdultIncomeTypesEnum.Other;
        break;
      default:
        break;
    }
    return adultIncomeTypeID;
  }

  pageHasBeenCompleted(app: FreeReducedMealApp, page: FreeReducedMealAppPage, selectedChild: FreeReducedMealAppChild, selectedAdult: FreeReducedMealAppAdult): boolean {
    return app.CompletedPages.find(o => o.PageID == page.ID
      && (!page.IsSingleChildPage || (selectedChild && o.ChildID == selectedChild.ID))
      && (!page.IsSingleAdultPage || (selectedAdult && o.AdultID == selectedAdult.ID))) != null;
  }

  sendAcceptedEmail(app: FreeReducedMealApp, children: FreeReducedMealAppChild[] = []) {
    if (children.length == 0) {
      this.getApplicationLunchStatusById(app.ID).pipe(take(1)).subscribe(c => {
        this.generateEmailBodyAndSend(app, c);
      });
    }
    else {
      this.generateEmailBodyAndSend(app, children);
    }
  }


  generateEmailBodyAndSend(app: FreeReducedMealApp, children: FreeReducedMealAppChild[]) {
    let body = '';
    const useSpanish = (app.FamilyLanguageId && app.FamilyLanguageId == 2) || app.SendMailingsInSpanish;

    if (this.selectedLetterTemplate && children) {
      if ((!useSpanish && this.selectedLetterTemplate.Body1) || (useSpanish && this.selectedLetterTemplate.Body1Spanish)) {
        body += '<p>' + (useSpanish ? this.selectedLetterTemplate.Body1Spanish : this.selectedLetterTemplate.Body1) + '</p>';
      }

      children.forEach(child => {
          let lunchStatus: string;
          const gradeLabel: string = useSpanish ? 'Grado' : 'Grade';
          const statusLabel: string = useSpanish ? 'Estatus' : 'Status';
          if (useSpanish) {
            switch (child.LunchStatus.toLowerCase()) {
              case 'free':
                lunchStatus = 'Gratis';
                break;
              case 'reduced':
                lunchStatus = 'Precio Reducido';
                break;
              case 'full student':
                lunchStatus = 'Percio Total';
                break;
              default:
                lunchStatus = child.LunchStatus;
                break;
            }
          }
          else {
            if (child.LunchStatus.toLowerCase() == 'full student') {
              lunchStatus = 'Full Price';
            }
            else {
              lunchStatus = child.LunchStatus;
            }
          }

          body += '<strong style="margin-left: 15px; font-family: sans-serif;">' + child.LastName + ', ' + child.FirstName + '</strong>';
          body += '<ul>';
          body += '<li style="margin-left: 30px; margin-top: -15px; font-family: sans-serif;"> ' + gradeLabel + ' - <em>' + child.GradeLevel + '</em></li>';
          body += '<li style="margin-left: 30px; font-family: sans-serif;"> ' + statusLabel + ' - <em>' + lunchStatus + '</em></li>';
          body += '</ul>';
      });

      if ((!useSpanish && this.selectedLetterTemplate.Body2) || (useSpanish && this.selectedLetterTemplate.Body2Spanish)) {
        body += '<p>' + (useSpanish ? this.selectedLetterTemplate.Body2Spanish : this.selectedLetterTemplate.Body2) + '</p>';
      }

      // Organizing the parameters
      const params = new MailParameters();
      params.userId = this.userService.currentUser.Id;
      params.EmailFrom = this.userPreferencesService.generalPreferences && this.userPreferencesService.generalPreferences.EmailFrom.toLowerCase() == 'no reply' ? this.loginService.LoginSettings.DefaultEmailReplyToAddress : this.userService.currentUser.Email;
      params.EmailTo = app.SubmittedByEmail;
      params.DisplayEmailToOnComment = 'To: ' + app.SubmittedByEmail;
      if (useSpanish) {
        params.Subject = 'Se acepta la presentación de la solicitud de comidas gratis / a precio reducido';
      }
      else {
        params.Subject = 'Free/Reduced Meal Application Submission Accepted';
      }
      params.Body = body;
      params.HtmlBody = true;

      // Sending the email
      this.subscriptions.push(
        this.sendEmailService.sendEmail(params, 'Free/Reduced Meal Application', true).subscribe(
          (result) => {
            if (result) {
              this.sharedService.showGoodSnackbar('Email sent');
            } else {
              this.sharedService.showBadSnackbar('Email failed to send');
            }
          },
          () => {
            this.sharedService.showBadSnackbar('Email failed to send');
          }
        )
      );
    }
    else {
      this.sharedService.showBadSnackbar('Email failed to send');
    }
  }

  checkForStudentChange(application: FreeReducedMealApp, students: Student[]): boolean {
    const studentsInFamily = students.map(o => o.Id);
    const enrolledStudentsInFamily = students.filter(o => o.EnrollDate).map(o => o.Id);
    const studentsOnForm = application.Children.filter(o => o.StudentID).map(o => o.StudentID);
    if (application.ID > 0 && enrolledStudentsInFamily.find(o => !studentsOnForm.includes(o))) {
      // new enrolled students were added to the family that were not on this application previously
      return true;
    }
    if (application.ID > 0 && studentsOnForm.find(o => !studentsInFamily.includes(o))) {
      // there are students on the application that are no longer associated with the family
      return true;
    }
    return false;
  }

  checkForParentChange(application: FreeReducedMealApp, parents: StudentContact[]): boolean {
      const parentsInFamily = parents.map(o => o.Id);
      const parentsOnForm = application.Adults.filter(o => o.PeopleID).map(o => o.PeopleID);
      if (application.ID > 0 && parentsInFamily.find(o => !parentsOnForm.includes(o))) {
        return true;  // parents were added to the family that were not on this application previously
      }
      if (application.ID > 0 && parentsOnForm.find(o => !parentsInFamily.includes(o))) {
        return true;  // there are parents on the application that are no longer associated with the family
      }
      return false;
  }

  getActiveFrmaPageById(pageId: number, yearId: number): Observable<FreeReducedMealAppPage> {
    const queryString = '?pageId=' + pageId
      + '&yearId=' + yearId;
    return this.http.get<FreeReducedMealAppPage>(System.apiURL.Admin + 'GetActiveFrmaPageById' + queryString);
  }

  getDefaultFrmaPages(): Observable<FreeReducedMealAppPage[]> {
    return this.http.get<FreeReducedMealAppPage[]>(System.apiURL.Admin + 'GetDefaultFrmaPages');
  }

  getAllFrmaUserTemplates(yearId: number): Observable<FreeReducedMealAppPage[]> {
    const queryString = '?yearId=' + yearId;
    return this.http.get<FreeReducedMealAppPage[]>(System.apiURL.Admin + 'GetAllFrmaUserTemplates' + queryString);
  }

  updateFrmaUserTemplate(yearId: number, page: FreeReducedMealAppPage): Observable<FreeReducedMealAppPage> {
    const queryString = '?yearId=' + yearId;
    return this.http.post<FreeReducedMealAppPage>(System.apiURL.Admin + 'UpdateFrmaUserTemplate' + queryString, page);
  }

  setActiveFrmaTemplate(yearId: number, page: FreeReducedMealAppPage): Observable<FreeReducedMealAppPage> {
    const queryString = '?yearId=' + yearId;
    return this.http.post<FreeReducedMealAppPage>(System.apiURL.Admin + 'SetActiveFrmaTemplate' + queryString, page);
  }

  getFrmaPageGroups(): Observable<FreeReducedMealAppPageGroup[]> {
    return this.http.get<FreeReducedMealAppPageGroup[]>(System.apiURL.Admin + 'GetFrmaPageGroups');
  }

  getFrmaPages(yearId: number): Observable<FreeReducedMealAppPage[]> {
    const queryString = '?yearId=' + yearId;
    return this.http.get<FreeReducedMealAppPage[]>(System.apiURL.Admin + 'GetFrmaPages' + queryString);
  }

  getFrmaPrograms(): Observable<FreeReducedMealAppProgram[]> {
    return this.http.get<FreeReducedMealAppProgram[]>(System.apiURL.Admin + 'GetFrmaPrograms');
  }

  getFrmaChildIncomeTypes(): Observable<FreeReducedMealAppChildIncomeType[]> {
    return this.http.get<FreeReducedMealAppChildIncomeType[]>(System.apiURL.Admin + 'GetFrmaChildIncomeTypes');
  }

  getFrmaAdultIncomeTypes(): Observable<FreeReducedMealAppAdultIncomeType[]> {
    return this.http.get<FreeReducedMealAppAdultIncomeType[]>(System.apiURL.Admin + 'GetFrmaAdultIncomeTypes');
  }

  getFrmaAdultIncomeSubTypes(): Observable<FreeReducedMealAppAdultIncomeSubType[]> {
    return this.http.get<FreeReducedMealAppAdultIncomeSubType[]>(System.apiURL.Admin + 'GetFrmaAdultIncomeSubTypes');
  }

  saveFrmaApplication(app: FreeReducedMealApp): Observable<FreeReducedMealApp> {
    return this.http.post<FreeReducedMealApp>(System.apiURL.Admin + 'SaveFrmaApplication', app);
  }

  staffUpdateFrmaApplication(app: FreeReducedMealApp): Observable<FreeReducedMealApp> {
    return this.http.post<FreeReducedMealApp>(System.apiURL.Admin + 'StaffUpdateApplication', app);
  }

  getFrmaAppsByFamilyID(familyId: number, yearId: number): Observable<FreeReducedMealApp[]> {
    const queryString = '?familyId=' + familyId
      + '&yearId=' + yearId;
    return this.http.get<FreeReducedMealApp[]>(System.apiURL.Admin + 'GetFrmaAppsByFamilyID' + queryString);
  }

  getFrmaAppWithPdf(appId: number): Observable<FreeReducedMealApp> {
    const queryString = '?appId=' + appId;
    return this.http.get<FreeReducedMealApp>(System.apiURL.Admin + 'GetFrmaAppWithPdf' + queryString);
  }

  getFrmaAppsByYearID(yearId: number, siteId: number, showCountsForAllSchools: boolean): Observable<FreeReducedMealApp[]> {
    const queryString = '?yearId=' + yearId
      + '&siteId=' + siteId
      + '&showCountsForAllSchools=' + showCountsForAllSchools;
    return this.http.get<FreeReducedMealApp[]>(System.apiURL.Admin + 'GetFrmaAppsByYearID' + queryString);
  }

  getFrmaApplication(appId: number): Observable<FreeReducedMealApp> {
    const queryString = '?appId=' + appId;
    return this.http.get<FreeReducedMealApp>(System.apiURL.Admin + 'GetFrmaApplication' + queryString);
  }

  getFRMAStudentStatus(familyId: number, yearId: number): Observable<FreeReducedMealAppStudentLunchStatus[]> {
    const queryString = '?familyId=' + familyId
      + '&yearId=' + yearId;
    return this.http.get<FreeReducedMealAppStudentLunchStatus[]>(System.apiURL.Admin + 'GetFRMAStudentStatus' + queryString);
  }

  deleteFrmaApplication(appId: number): Observable<boolean> {
    const queryString = '?appId=' + appId;
    return this.http.get<boolean>(System.apiURL.Admin + 'DeleteFrmaApplication' + queryString);
  }

  getFrmaPreviousChildren(familyId: number, yearId: number): Observable<FreeReducedMealAppChild[]> {
    const queryString = '?familyId=' + familyId
      + '&yearId=' + yearId;
    return this.http.get<FreeReducedMealAppChild[]>(System.apiURL.Admin + 'GetFrmaPreviousChildren' + queryString);
  }

  getFrmaPreviousAdults(familyId: number, yearId: number): Observable<FreeReducedMealAppAdult[]> {
    const queryString = '?familyId=' + familyId
      + '&yearId=' + yearId;
    return this.http.get<FreeReducedMealAppAdult[]>(System.apiURL.Admin + 'GetFrmaPreviousAdults' + queryString);
  }

  deletFrmaeUserTemplate(userTemplateId: number): Observable<boolean> {
    const queryString = '?userTemplateId=' + userTemplateId;
    return this.http.get<boolean>(System.apiURL.Admin + 'DeletFrmaeUserTemplate' + queryString);
  }

  getPageChangeHistory(userTemplateId: number): Observable<FreeReducedMealAppPageChangeHistory[]> {
    const queryString = '?userTemplateId=' + userTemplateId;
    return this.http.get<FreeReducedMealAppPageChangeHistory[]>(System.apiURL.Admin + 'GetPageChangeHistory' + queryString);
  }

  getFrmaFamilies(yearId: number): Observable<FreeReducedMealAppFamily[]> {
    const queryString = '?yearId=' + yearId;
    return this.http.get<FreeReducedMealAppFamily[]>(System.apiURL.Admin + 'GetFrmaFamilies' + queryString);
  }

  getFrmaLetterTemplateById(templateId: number): Observable<FRMALetterTemplate> {
    const queryString = '?templateId=' + templateId;
    return this.http.get<FRMALetterTemplate>(System.apiURL.Admin + 'GetFrmaLetterTemplateById' + queryString);
  }

  getApplicationLunchStatusById(appId: number): Observable<FreeReducedMealAppChild[]> {
    const queryString = '?appId=' + appId;
    return this.http.get<FreeReducedMealAppChild[]>(System.apiURL.Admin + 'GetApplicationLunchStatusById' + queryString);
  }

  getProjectedLunchStatusById(appId: number): Observable<FreeReducedMealAppChild[]> {
    const queryString = '?appId=' + appId;
    return this.http.get<FreeReducedMealAppChild[]>(System.apiURL.Admin + 'GetProjectedLunchStatusById' + queryString);
  }

  acceptApplicationAndMergeIntoClassic(appId: number): Observable<boolean> {
    const queryString = '?appId=' + appId;
    return this.http.get<boolean>(System.apiURL.Admin + 'AcceptApplicationAndMergeIntoClassic' + queryString);
  }

  getFRMAYearsForParent(familyId: number): Observable<Year[]> {
    const queryString = '?familyId=' + familyId;
    return this.http.get<Year[]>(System.apiURL.Admin + 'GetFRMAYearsForParent' + queryString);
  }

  getFRMAStudentsByFamilyId( yearId: number, familyId: number): Observable<Student[]> {
    const queryString = '?yearId=' + yearId
    + '&familyId=' + familyId;
    return this.http.get<Student[]>(System.apiURL.Admin + 'GetFRMAStudentsByFamilyId' + queryString);
  }

  getAcceptanceHistoryByAppID(appId: number): Observable<FreeReducedMealAppAcceptedHistory[]> {
    const queryString = '?appId=' + appId;
    return this.http.get<FreeReducedMealAppAcceptedHistory[]>(System.apiURL.Admin + 'GetAcceptanceHistoryByAppID' + queryString);
  }

  addAcceptToApplication(appId: number): Observable<number> {
    const queryString = '?appId=' + appId;
    return this.http.get<number>(System.apiURL.Admin + 'AddAcceptToApplication' + queryString);
  }
}
